import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
} from '@mui/material';

// Define mapping objects for standardized headers
const expenseHeaderMappings = {
    "Expenses": "expenses_subheader",
    "Advertising & Promotion": "advertising & promotion",
    "Advertising": "advertising",
    "Automotive Expense": "automotive expense",
    "Car and truck expenses": "Car and truck expenses",
    "Mileage": "mileage",
    "Bad debts": "bad debts",
    "Bank & Service Charges": "bank & service charges",
    "Merchant fees": "merchant fees",
    "Cash Over/Short": "Cash Over/Short",
    "Charitable Contributions": "charitable contributions",
    "Gifts to charity": "gifts to charity",
    "Commissions & Bonus": "commissions & bonus",
    "Commissions and fees": "commissions & fees",
    "Computer & Internet": "computer & internet",
    "Software expense": "software expense",
    "Website expense": "website expense",
    "Contractors": "contractors",
    "Contract labor": "contract labor",
    "Delivery": "delivery",
    "Disposal & Waste Fees": "disposal & waste fees",
    "Dues & Subscriptions": "dues & subscriptions",
    "Education & training": "education & training",
    "Employee Benefit Programs": "employee benefit programs",
    "Equipment Rental": "equipment rental",
    "Gifts": "gifts",
    "Insurance - Automotive": "insurance - automotive",
    "Insurance - General Liability": "insurance - general liability",
    "Insurance (other than health)": "insurance (other than health)",
    "Insurance - Life": "insurance - life",
    "Insurance - Medical": "insurance - medical",
    "Self-employed health insurance deduction": "self-employed health insurance deduction",
    "Insurance - Unemployment": "insurance - unemployment",
    "Insurance - Workers Comp": "insurance - workers comp",
    "Inventory Shrinkage": "inventory shrinkage",
    "Janitorial Services": "janitorial services",
    "Laundry & cleaning": "laundry & cleaning",
    "Licenses & Permits": "licenses & permits",
    "Management Fee": "management fee",
    "Meals & Entertainment": "meals & entertainment",
    "Meals (after limitations)": "meals (after limitations)",
    "Meetings & Conferences": "meetings & conferences",
    "Miscellaneous": "miscellaneous",
    "Other deductions (see instructions—attach statement)": "other deductions",
    "Other itemized deductions": "other itemized deductions",
    "Other adjustments (see instructions—attach statement)": "other adjustments",
    "Office Expenses": "office expenses",
    "Operating Supplies": "operating supplies",
    "Supplies": "supplies",
    "Outside Services": "outside services",
    "Legal and professional services": "legal and professional services",
    "Parking & Tolls": "parking & tolls",
    "Payroll Processing Fees": "payroll processing fees",
    "Penalties": "penalties",
    "Penalty on early withdrawal of savings": "penalty on early withdrawal of savings",
    "Pest Control": "pest control",
    "Profit Sharing": "profit sharing",
    "Pension": "pension",
    "Self-employed SEP": "self-employed sep",
    "Rent": "rent",
    "Rents": "rents",
    "Rental expenses (attach Form 8825)": "rental expenses",
    "Rent - Base Rent": "rent - base rent",
    "Rent - Additional Rent (CAM/NNN)": "rent - additional rent (cam/nnn)",
    "Recruiting Expense": "recruiting expense",
    "Repairs & Maintenance": "repairs & maintenance",
    "Royalties": "royalties",
    "Royalties expense": "royalties expense",
    "Salaries & Wages - Employees": "salaries & wages - employees",
    "Salaries and wages (less employment credits)": "salaries and wages",
    "Compensation of officers (attach Form 1125-E)": "salaries & wages - officers",
    "Security": "security",
    "Small tools & equipment": "small tools & equipment",
    "Taxes & Licenses": "taxes & licenses",
    "Taxes - Payroll": "taxes - payroll",
    "Taxes - Property": "taxes - property",
    "State and local taxes (real estate, personal property, income)": "state and local taxes",
    "Taxes - Other": "taxes - other",
    "Telephone": "telephone",
    "Travel": "travel",
    "Uniforms": "uniforms",
    "Utilities": "utilities",
    "Additional Expenses (IRS-Specific)": "additional expenses",
    "Guaranteed payments to partners": "guaranteed payments to partners",
    "Section 179 expense deduction (from Form 4562)": "cost of qualifying equipment/software",
    "Domestic production activities deduction (attach Form 8903)": "domestic production activities",
    "Educator expenses": "educator expenses",
    "Certain business expenses of reservists": "certain business expenses of reservists",
    "Health savings account deduction (attach Form 8889)": "health savings account deduction",
    "Moving expenses for Armed Forces (attach Form 3903)": "moving expenses for armed forces",
    "Deductible part of self-employment tax": "deductible part of self-employment tax",
    "Alimony paid": "alimony paid",
    "IRA deduction": "ira deduction",
    "Student loan interest deduction": "student loan interest deduction",
    "Archer MSA deduction (attach Form 8853)": "archer msa deduction",
    "Total deductions (add applicable deduction lines)": "total expenses",
}

const otherExpenseHeaderMappings = {
    "Interest (see instructions)": "interest expense",
    "Home mortgage interest": "home mortgage expense",
    "Investment interest": "investment interest",
    "Total tax (see instructions)": "tax expense",
    "Self-employment tax (attach Schedule SE)": "self employment tax",
    "Alternative minimum tax (attach Form 4626)": "alternative minimum",
    "Additional tax on IRAs": "additional tax on expense",
    "Household employment taxes (attach Schedule H)": "household employment taxes",
    "Other taxes (see instructions—attach statement)": "other taxes",
    "Depreciation & Amortization": "depreciation & amortization",
    "Depreciation from Form 4562 not claimed on Form 1125-A or elsewhere on return (attach Form 4562)": "depreciation",
    "Depletion (do not deduct oil and gas depletion)": "depletion",
    "Casualty and theft losses (attach Form 4684)": "casualty and theft ",
    "Gambling losses (limited to gambling winnings)": "gambling losses",
    "Estimated tax penalty": "estimated tax penality",
    "Amount paid with extension request (Form 8868)": "extention request payments",
    "Tax from Schedule D (Form 1120-S)": "tax on net capital gains",
    "Tax from Form 4255 (Recapture of Investment Credit)": "additional tax payable (previously reclaimed)",
    "Interest due under look-back method—completed long-term contracts": "interest due under look back method contracts)",
    "Interest due under look-back method—property depreciated under income forecast method": "interest due under look back method (property)",
    "Foreign tax credit (attach Form 1118)": "foreign tax credit ",
    "General business credit (attach Form 3800)": "general business credit",
    "Elective payment election amount from Form 3800": "payment received from irs"
}

// Additional mappings for sales, COGS, and income
const salesCogsHeaderMappings = {
    "Sales": "Sales",
    "Gross receipts or sales": "Gross Receipts or Sales",
    "Cost of goods sold": "Cost of Goods Sold",
    "Gross profit": "Gross Profit",
    "Gross profit margin": "Gross Profit Margin"
};

const incomeHeaderMappings = {
    "Gross rents": "gross rents",
    "Gross royalties": "gross royalties",
    "Other income (loss) (see instructions—attach statement)": "total other income (loss)",
    "Other Income": "other income",
    "Sales Tax Vendor Rebate": "sales tax vendor rebate",
    "Taxable refunds": "taxable refunds",
    "Gambling income (including lottery winnings) (attach Form W-2G)": "gambling income (including lottery winnings)",
    "Dividends (attach Schedule C)": "dividends",
    "Interest": "interest",
    "Capital gain net income (attach Schedule D)": "capital gain",
    "Net gain (loss) from Form 4797": "net gain (loss) from sale of business property",
    "Wages": "wages, tips, other compensation",
    "Tax-exempt interest": "interest income",
    "Ordinary dividends": "dividends - ordinary",
    "Qualified dividends": "dividends - qualified",
    "Alimony received": "alimony received",
    "Other gains or (losses) (attach Form 4797)": "gain (loss) from sale of business property",
    "Rental real estate": "other rental income",
    "Farm income or (loss) (attach Schedule F)": "farm income",
    "Unemployment compensation": "unemployment compensation",
    "Social security benefits": "social security benefits",
    "Taxable amount of social security benefits": "taxable amount of social security benefits",
    "Net rental real estate income (loss) (from Schedule K, Form 1065 or 1120-S)": "rental real estate income",
    "Other separately stated income items (from Schedule K-1, Form 1065 or 1120-S)": "other income from partnerships/s-corps",
    "Portfolio income (interest, dividends, royalties, etc.)": "portfolio income (interest, dividends, royalties, etc.)",
    "Other portfolio income (loss)": "other portfolio income (loss)",
    "Cancellation of debt income (attach Form 1099-C)": "cancellation of debt",
    "Barter income (see instructions—attach statement)": "barter income",
    "REMIC excess inclusions (from Schedule K-1, Form 1065)": "income from a real estate mortgage investment conduit",
    "Digital assets (see instructions—attach statement)": "income from digital assets",
    "Jury duty pay": "jury duty pay",
    "Foreign earned income exclusion (attach Form 2555)": "foreign earned income",
    "Earned income credit": "income credits",
    "Additional child tax credit": "child tax credit",
    "Net premium tax credit": "premium tax credit",
    "Credit for child and dependent care expenses (attach Form 2441)": "credit for child & dependent care expenses",
    "Education credits (attach Form 8863)": "education credits",
    "Retirement savings contributions credit (attach Form 8880)": "retirement savings contributions credit",
    "Child tax credit": "child tax credit",
    "Credit for other dependents": "credit for other dependents",
    "Clean vehicle credit (attach Form 8936)": "clean vehicle credit",
    "Credit for small employer health insurance premiums (attach Form 8941)": "credit for small employer health insurance premiums",
    "Work opportunity credit (attach Form 5884)": "work opportunity credit ",
    "Unused investment credit from clean electricity credit allocated from cooperatives": "unused investment credit from clean electricity credit allocated from cooperatives",
    "Low-income housing credit (from Form 8586)": "low-income housing credit",
    "Other credits (from Schedule K-1, Form 1065 or 1120-S)": "other credits",
    "Taxes - Income": "taxes income",
};

// Format account head for display: trim and remove periods
const formatAccountHeadForDisplay = (head) => {
    if (!head) return '';
    return head.trim().replace(/\./g, '');
};

// NEW FUNCTION: Capitalize each word in the account head
const formatAccountHead = (head) => {
    if (!head || head === "---") return head || '';
    const words = head.split(" ");
    return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

// Get standardized header title from mappings
const getStandardizedHeaderTitle = (head) => {
    if (!head) return '';

    const formattedHead = formatAccountHeadForDisplay(head);
    const headLower = formattedHead.toLowerCase();

    // Special case handling for specific headers
    if (headLower === 'expenses' || headLower === 'expenses_subheader') return 'Expenses';
    if (headLower === 'other income/expense' || headLower === 'other_income_expense_subheader') return 'Other Income/Expense';
    if (headLower === 'add backs (seller discretionary expenses)' || headLower === 'add_backs_subheader') return 'Add Backs (Seller Discretionary Expenses)';
    if (headLower === 'total add backs') return 'Total Add Backs';
    if (headLower === 'seller discretionary cash flow') return 'Seller Discretionary Cash Flow';
    if (headLower === 'ebitda') return 'EBITDA';
    if (headLower === 'net income') return 'Net Income';

    // Check in sales/COGS mappings first
    for (const [key, value] of Object.entries(salesCogsHeaderMappings)) {
        if (key.toLowerCase() === headLower) {
            return value;
        }
    }

    // Check in otherExpenseHeaderMappings (priority over expenseHeaderMappings)
    for (const [key, value] of Object.entries(otherExpenseHeaderMappings)) {
        if (key.toLowerCase() === headLower) {
            return value;
        }
    }

    // Check in expenseHeaderMappings
    for (const [key, value] of Object.entries(expenseHeaderMappings)) {
        if (key.toLowerCase() === headLower) {
            return value;
        }
    }

    // Check in incomeHeaderMappings
    for (const [key, value] of Object.entries(incomeHeaderMappings)) {
        if (key.toLowerCase() === headLower) {
            return value;
        }
    }

    // If no mapping found, return the original formatted head
    return formattedHead;
};

const ExcelPreview = ({ excelData, businessType }) => {
    const [processedData, setProcessedData] = useState(null);

    useEffect(() => {
        if (excelData && excelData.tableData && excelData.columns) {
            // Process data to ensure % Sales is calculated consistently
            // and apply standardized header titles
            processTableData(excelData);
        }
    }, [excelData, businessType]);

    const processTableData = (data) => {
        if (!data || !data.tableData || !data.columns) return;

        const { tableData, columns } = data;

        // Apply standardized header titles and capitalization
        const standardizedTableData = tableData.map(row => {
            const newRow = { ...row };
            const originalHead = row['Account Head'];

            // Replace 'Account Head' with standardized title
            const standardizedHead = getStandardizedHeaderTitle(originalHead);
            
            // Apply capitalization to the standardized header
            newRow['Account Head'] = formatAccountHead(standardizedHead);

            return newRow;
        });

        // Now calculate percentages using the standardized data
        calculatePercentages({
            tableData: standardizedTableData,
            columns: columns,
            businessType: data.businessType
        });
    };

    const calculatePercentages = (data) => {
        if (!data || !data.tableData || !data.columns) return;

        const { tableData, columns } = data;

        // Find the Sales reference column (either the latest year or 2-year average)
        const salesReferenceCol = findSalesReferenceColumn(tableData, columns);
        if (!salesReferenceCol) return;

        // Find the % Sales column index
        const percentColIndex = columns.indexOf('% Sales');
        if (percentColIndex === -1) return;

        // Find the Sales row to use as reference
        const salesRow = tableData.find(row => {
            const accountHead = row['Account Head'] || '';
            return accountHead.toLowerCase() === 'sales';
        });
        
        if (!salesRow) return;

        const salesAmount = parseNumericValue(salesRow[salesReferenceCol]);
        if (salesAmount === 0) return;

        // Deep copy tableData to safely modify
        const newTableData = JSON.parse(JSON.stringify(tableData));

        // Calculate percentages for all rows
        for (let row of newTableData) {
            const accountHead = (row['Account Head'] || '').toLowerCase();
            
            // Skip headers
            if (['expenses', 'other income/expense', 'add backs (seller discretionary expenses)'].includes(accountHead)) {
                continue;
            }

            // Special case for Gross Profit Margin
            if (accountHead === 'gross profit margin') {
                const grossProfitRow = newTableData.find(r => (r['Account Head'] || '').toLowerCase() === 'gross profit');
                if (grossProfitRow) {
                    const grossProfitAmount = parseNumericValue(grossProfitRow[salesReferenceCol]);

                    // Calculate for all columns
                    for (let col of columns) {
                        if (col !== 'Account Head' && col !== '% Sales' && col !== 'Mark as SDE') {
                            const colSalesAmount = parseNumericValue(salesRow[col]);
                            const colGrossProfitAmount = parseNumericValue(grossProfitRow[col]);
                            if (colSalesAmount !== 0) {
                                row[col] = formatPercentage(colGrossProfitAmount / colSalesAmount);
                            } else {
                                row[col] = '0.00%';
                            }
                        }
                    }

                    // Also update % Sales column
                    row['% Sales'] = formatPercentage(grossProfitAmount / salesAmount);
                }
            } else {
                // Calculate % Sales for regular rows using the reference column
                const rowAmount = parseNumericValue(row[salesReferenceCol]);
                row['% Sales'] = formatPercentage(rowAmount / salesAmount);
            }
        }

        // Filter out rows where all numeric values are zero
        // But keep important rows and header rows
        const importantRows = [
            'sales', 'cost of goods sold', 'gross profit', 'gross profit margin',
            'ebitda', 'net income', 'total add backs',
            'seller discretionary cash flow', 'expenses', 'other income/expense',
            'add backs (seller discretionary expenses)'
        ];

        const filteredTableData = newTableData.filter(row => {
            const accountHead = (row['Account Head'] || '').toLowerCase();

            // Always keep important rows and header rows
            if (importantRows.some(important => accountHead.includes(important))) {
                return true;
            }

            // Check if all values in the row are zero
            const allZero = columns.every(col => {
                // Skip non-numeric columns
                if (col === 'Account Head' || col === 'Mark as SDE') {
                    return true;
                }

                // Check if value is zero
                const value = parseNumericValue(row[col]);
                return value === 0;
            });

            // Keep row if not all values are zero
            return !allZero;
        });

        setProcessedData({
            tableData: filteredTableData,
            columns: data.columns,
            businessType: data.businessType
        });
    };

    const findSalesReferenceColumn = (tableData, columns) => {
        // First try to find Average column if it exists
        const averageCol = columns.find(col => col.includes('Average'));
        if (averageCol) return averageCol;

        // If no average, find most recent year (assuming years are in descending order)
        const yearCols = columns.filter(col =>
            col !== 'Account Head' &&
            col !== '% Sales' &&
            col !== 'Mark as SDE'
        );

        return yearCols.length > 0 ? yearCols[0] : null;
    };

    const parseNumericValue = (value) => {
        if (!value) return 0;
        if (typeof value === 'number') return value;

        // Handle formatted strings like "$1,234" or "1,234.56%"
        return parseFloat(String(value).replace(/[$,%]/g, '')) || 0;
    };

    const formatPercentage = (value) => {
        return (value * 100).toFixed(2) + '%';
    };

    // Helper function to determine cell styling based on row/column
    const getCellStyle = (row, colName) => {
        const accountHead = row['Account Head'] || '';
        const accountHeadLower = accountHead.toLowerCase();
        const isHeaderCol = colName === 'Account Head';

        // Base styles
        const styles = {
            fontWeight: 'normal',
            color: 'inherit',
            padding: '5px 10px',
            textAlign: isHeaderCol ? 'left' : 'right',
            fontStyle: 'normal',
            whiteSpace: 'nowrap',
            borderBottom: '1px solid #e0e0e0',
        };

        // Safety check for null values
        if (!accountHead) return styles;

        // Add indentation for sub-categories
        if (isHeaderCol) {
            // Sales subheads
            const salesSubheads = [
                'beverage sales', 'tobacco sales', 'cigarettes sales',
                'liquor sales', 'merchandise sales', 'lottery sales',
                'sales tax collection'
            ];

            // COGS subheads
            const cogsSubheads = [
                'beverage purchases', 'tobacco purchases', 'cigarettes purchase',
                'liquor purchases', 'food purchases', 'lottery purchases',
                'merchant account fees', 'cost of goods sold freight',
                'mixers and misc purchases'
            ];

            // Add back subheads
            const addBackHeads = [
                'bank & service charges', 'charitable contributions', 'depreciation & amortization',
                'insurance - medical', 'insurance - life', 'interest', 'meals & entertainment',
                'miscellaneous', 'professional fees', 'salaries & wages - officers',
                'telephone', 'travel', 'taxes - other', 'depreciation from form',
                'energy efficient commercial buildings', 'ordinary business income',
                'rents', 'repairs and maintenance', 'taxes and licenses', 'other deductions',
                'total deductions', 'total income', 'employee benefit programs',
                'pension, profit-sharing, etc, plans', 'returns and allowances'
            ];

            const displayData = processedData ? processedData.tableData : excelData.tableData;
            const netIncomeIndex = displayData.findIndex(r => 
                (r['Account Head'] || '').toLowerCase().includes('net income')
            );
            
            const currentRowIndex = displayData.findIndex(r => 
                r['Account Head'] === accountHead
            );

            if (
                salesSubheads.some(item => accountHeadLower.includes(item)) ||
                (businessType === 'Retail' && cogsSubheads.some(item => accountHeadLower.includes(item))) ||
                (netIncomeIndex !== -1 && currentRowIndex > netIncomeIndex &&
                    addBackHeads.some(item => accountHeadLower.includes(item) && !accountHeadLower.includes('total add backs')))
            ) {
                styles.paddingLeft = '30px';
            }
        }

        // Section headers
        if (['expenses', 'other income/expense', 'other income total', 'add backs (seller discretionary expenses)'].some(header => accountHeadLower.includes(header))) {
            styles.fontWeight = 'bold';
            styles.fontStyle = 'italic';
            styles.backgroundColor = '#f5f5f5';
            return styles;
        }

        // Major categories
        if (['sales', 'cost of goods sold'].some(cat => accountHeadLower.includes(cat))) {
            styles.fontWeight = 'bold';
            styles.color = '#0000FF'; // Blue for sales and COGS
            return styles;
        }

        // Totals and metrics
        if (['gross profit', 'gross profit margin', 'total expenses', 'ebitda',
            'net income', 'total add backs', 'seller discretionary cash flow'].some(total => accountHeadLower.includes(total))) {
            styles.fontWeight = 'bold';
            return styles;
        }

        return styles;
    };

    // Helper function to format cell values
    const formatCellValue = (value, isPercentage = false) => {
        if (value === undefined || value === null || value === '') {
            return isPercentage ? '0.00%' : '$0';
        }

        if (typeof value === 'string') {
            if (value.includes('%')) return value;
            if (value.includes('$')) return value;

            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                return isPercentage ?
                    (numValue * 100).toFixed(2) + '%' :
                    '$' + Math.round(numValue).toLocaleString();
            }
            return value;
        }

        if (typeof value === 'number') {
            return isPercentage ?
                (value * 100).toFixed(2) + '%' :
                '$' + Math.round(value).toLocaleString();
        }

        return isPercentage ? '0.00%' : '$0';
    };

    if (!excelData || !excelData.tableData || !excelData.columns) {
        return (
            <Box sx={{ my: 2 }}>
                <Alert severity="error">
                    Failed to fetch Excel data: Request failed with status code 400
                </Alert>
            </Box>
        );
    }

    // Use processed data if available, otherwise fall back to original data
    const displayData = processedData || excelData;
    const { tableData, columns } = displayData;

    return (
        <Box sx={{ my: 3 }}>
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    maxHeight: 600,
                    overflow: 'auto',
                    mb: 3,
                    border: '1px solid #d1d1d1',
                    borderRadius: 1,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
            >
                <Table size="small" sx={{ minWidth: 800 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                            {columns.map((column, index) => {
                                if (column === 'Mark as SDE') {
                                    return null;
                                }
                                return (
                                    <TableCell
                                        key={index}
                                        align={index === 0 ? 'left' : 'right'}
                                        sx={{
                                            fontWeight: 'bold',
                                            color: '#333',
                                            whiteSpace: 'nowrap',
                                            borderBottom: '2px solid #d1d1d1',
                                            padding: '5px 10px',
                                        }}
                                    >
                                        {column}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableData.map((row, rowIndex) => {
                            // Ensure row['Account Head'] exists before checking if it's a header row
                            const accountHead = row['Account Head'] || '';
                            const accountHeadLower = accountHead.toLowerCase();
                            const isHeaderRow = ['expenses', 'other income/expense', 'add backs (seller discretionary expenses)'].some(
                                header => accountHeadLower === header.toLowerCase()
                            );

                            return (
                                <TableRow
                                    key={rowIndex}
                                    sx={{
                                        backgroundColor: isHeaderRow ? '#f5f5f5' : 'inherit',
                                        '&:nth-of-type(odd)': !isHeaderRow ? { backgroundColor: '#fafafa' } : {},
                                        '&:hover': {
                                            backgroundColor: '#f0f7ff',
                                        }
                                    }}
                                >
                                    {columns.map((column, colIndex) => {
                                        if (column === 'Mark as SDE') {
                                            return null;
                                        }

                                        // Special formatting for header rows
                                        if (isHeaderRow && colIndex !== 0) {
                                            return (
                                                <TableCell
                                                    key={colIndex}
                                                    align="right"
                                                    sx={{
                                                        ...getCellStyle(row, column),
                                                    }}
                                                >
                                                    {''}
                                                </TableCell>
                                            );
                                        }

                                        return (
                                            <TableCell
                                                key={colIndex}
                                                align={colIndex === 0 ? 'left' : 'right'}
                                                sx={{
                                                    ...getCellStyle(row, column),
                                                }}
                                            >
                                                {colIndex === 0
                                                    ? row[column] // Already formatted with capitalization in processTableData
                                                    : formatCellValue(row[column], column === '% Sales' || accountHeadLower.includes('gross profit margin'))
                                                }
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mb: 3 }}>
                This is a preview of the financial summary. Download the Excel file for the complete report with formulas and editing capabilities.
            </Alert>
        </Box>
    );
};

export default ExcelPreview;